import { useCallback, useMemo } from 'react';
import { createGoogleClient } from '../utils';

declare global {
  interface Window {
    google?: typeof google;
    initMap?: () => void;
  }
}

interface UseGooglePlacesConfig {
  countryRestriction?: string;
  baseUrl?: string;
}

interface GetGooglePlaceIDOptions {
  input: string;
}

interface GooglePlaceItem {
  description: string;
  place_id: string;
  structured_formatting?: {
    main_text: string;
    secondary_text: string;
  };
}

interface GetPlaceDetailsOptions {
  searchData?: GooglePlaceItem;
  placeId: string;
  fields: string[];
}

interface PlaceDetails {
  formattedAddress?: string;
  formatted_address?: string;
  addressComponents?: {
    longText: string;
    shortText?: string;
    types?: string[];
    languageCode?: string;
  }[];

  nationalPhoneNumber?: string;
  websiteUri?: string;

  regularOpeningHours?: {
    openNow?: boolean;
    periods?: {
      open?: { day: number; hour: number; minute: number };
      close?: { day: number; hour: number; minute: number };
    }[];
    weekdayDescriptions?: string[];
    nextOpenTime?: string;
  };

  googleMapsUri?: string;
  utcOffsetMinutes?: number;

  displayName?: {
    text: string;
    languageCode?: string;
  };
  name?: string;

  location?: {
    latitude: number;
    longitude: number;
  };
  geometry?: {
    location: {
      lat: number;
      lng: number;
    };
  };
  types: string[];
}

interface SearchPlacesTextOptions {
  query: string;
  maxResults?: number;
}

interface PlaceSearchResult {
  id: string;
  displayName: {
    text: string;
  };
  formattedAddress: string;
  types?: string[];
}

interface PlaceDetailsEnhanced {
  id: string;
  displayName: {
    text: string;
  };
  formattedAddress: string;
  rating?: number;
  userRatingCount?: number;
  nationalPhoneNumber?: string;
  websiteUri?: string;
  businessStatus?: string;
  types?: string[];
  reviews?: Array<{
    publishTime: string;
    authorAttribution: {
      displayName: string;
      uri: string;
      photoUri: string;
    };
    text: {
      text: string;
      languageCode: string;
    };
    originalText: {
      text: string;
      languageCode: string;
    };
    rating: number;
    authorResponse?: {
      text: {
        text: string;
        languageCode: string;
      };
      publishTime: string;
    };
  }>;
}

const useGooglePlaces = (config: UseGooglePlacesConfig) => {
  const { countryRestriction = 'US', baseUrl = '' } = config;

  const normalizedCountry =
    countryRestriction?.length === 2
      ? countryRestriction.toUpperCase()
      : countryRestriction;

  const googleClient = useMemo(() => createGoogleClient(baseUrl || ''), [baseUrl]);

  const getGooglePlaceID = useCallback(
    async ({ input }: GetGooglePlaceIDOptions): Promise<GooglePlaceItem[]> => {
      if (!input) return [];

      try {
        const response = await googleClient.get('/places/autocomplete', {
          params: {
            input,
            country: normalizedCountry,
          },
        });

        return response.data ?? [];
      } catch (error) {
        console.error('Google Places autocomplete error:', error);
        throw error;
      }
    },
    [googleClient, normalizedCountry],
  );

  const getPlaceDetails = useCallback(
    async ({ placeId, fields, searchData }: GetPlaceDetailsOptions): Promise<PlaceDetails> => {
      try {
        const response = await googleClient.get(`/places/details/${placeId}`, {
          params: fields && fields.length > 0 ? { fields: fields.join(',') } : undefined,
        });

        const raw = response.data;

        const formattedAddress =
          raw.formattedAddress ??
          raw.formatted_address ??
          searchData?.structured_formatting?.secondary_text;

        const mapped: PlaceDetails = {
          ...raw,
          formattedAddress,
        };

        return mapped;
      } catch (error) {
        console.error('Error loading place details:', error);
        throw error;
      }
    },
    [googleClient],
  );

  const searchPlacesText = useCallback(
    async ({ query, maxResults = 10 }: SearchPlacesTextOptions): Promise<PlaceSearchResult[]> => {
      if (!query || !query.trim()) return [];

      try {
        const response = await googleClient.get('/places/search', {
          params: {
            query,
            maxResults,
          },
        });

        return response.data ?? [];
      } catch (error) {
        console.error('Google Places text search error:', error);
        throw error;
      }
    },
    [googleClient],
  );

  return {
    isLoaded: !!baseUrl, 
    getGooglePlaceID,
    getPlaceDetails,
    searchPlacesText,
  };
};

export default useGooglePlaces;
export type {
  UseGooglePlacesConfig,
  GetGooglePlaceIDOptions,
  GooglePlaceItem,
  GetPlaceDetailsOptions,
  PlaceDetails,
  SearchPlacesTextOptions,
  PlaceSearchResult,
  PlaceDetailsEnhanced,
};
